import type { ChannelTabType } from "@bezoned/shared/constants/channel-tab-type.js";
import { ChannelTabTypes } from "@bezoned/shared/constants/channel-tab-type.js";
import type { ChannelTabListResponse } from "@bezoned/shared/types/api/channel/channel-tab-list.types.js";

import type { useTeamsNavigation } from "../../hooks/use-teams-navigation.js";
import { ContextMenuItem } from "./context-menu-item.jsx";

interface ContextMenuTabListProps {
  tabs: ChannelTabListResponse["tabs"] | null;
  displayName: string | null;
  channelUuid: string;
  msChannelId: string | null;
  navigateToChannelTab: ReturnType<
    typeof useTeamsNavigation
  >["navigateToChannelTab"];
  navigateToChannel: ReturnType<typeof useTeamsNavigation>["navigateToChannel"];
  isLoading: (id: string) => boolean;
}

interface TabItem {
  id: string;
  type: string;
  displayName: string;
  onClick: () => void;
  loading: boolean;
}

// Define the order in which tab types should appear
const TAB_TYPE_ORDER: ChannelTabType[] = [
  ChannelTabTypes.Files,
  ChannelTabTypes.Notes,
  ChannelTabTypes.Whiteboard,
  ChannelTabTypes.Planner,
];

const MUST_HAVE_ENTITY = new Set<string>([
  ChannelTabTypes.Notes,
  ChannelTabTypes.Whiteboard,
  ChannelTabTypes.Planner,
]);

interface ProcessTabsContext {
  tabs: ChannelTabListResponse["tabs"];
  msChannelId: string | null;
  navigateToChannelTab: ContextMenuTabListProps["navigateToChannelTab"];
  isLoading: ContextMenuTabListProps["isLoading"];
}

const processTabsOfType = (
  tabType: ChannelTabType,
  context: ProcessTabsContext
): TabItem[] => {
  const { tabs, msChannelId, navigateToChannelTab, isLoading } = context;
  const tabArray = tabs[tabType];

  if (!Array.isArray(tabArray)) {
    return [];
  }

  const requiresEntity = MUST_HAVE_ENTITY.has(tabType);

  return tabArray
    .filter((tab) => !requiresEntity || tab.entityId)
    .map((tab) => ({
      displayName: tab.displayName,
      id: tab.id,
      loading: isLoading(tab.id),
      onClick: () => msChannelId && navigateToChannelTab(msChannelId, tab),
      type: tab.type,
    }));
};

interface BuildTabListParams {
  tabs: ChannelTabListResponse["tabs"] | null;
  channelUuid: string;
  msChannelId: string | null;
  displayName: string | null;
  navigateToChannel: ContextMenuTabListProps["navigateToChannel"];
  navigateToChannelTab: ContextMenuTabListProps["navigateToChannelTab"];
  isLoading: ContextMenuTabListProps["isLoading"];
}

const buildTabList = ({
  tabs,
  channelUuid,
  msChannelId,
  displayName,
  navigateToChannel,
  navigateToChannelTab,
  isLoading,
}: BuildTabListParams): TabItem[] => {
  // Always start with Posts
  const result: TabItem[] = [
    {
      displayName: "Post",
      id: "posts",
      loading: isLoading(channelUuid),
      onClick: () =>
        msChannelId &&
        navigateToChannel(msChannelId, displayName ?? "conversations"),
      type: "posts",
    },
  ];

  if (!tabs) {
    return result;
  }

  const context: ProcessTabsContext = {
    isLoading,
    msChannelId,
    navigateToChannelTab,
    tabs,
  };

  // Add tabs in the defined order
  for (const tabType of TAB_TYPE_ORDER) {
    result.push(...processTabsOfType(tabType, context));
  }

  // Add any remaining tab types not in the ordered list
  const remainingTypes = Object.values(ChannelTabTypes).filter(
    (type) => !TAB_TYPE_ORDER.includes(type)
  );

  for (const tabType of remainingTypes) {
    result.push(...processTabsOfType(tabType, context));
  }

  return result;
};

export const ContextMenuTabList = ({
  tabs,
  channelUuid,
  msChannelId,
  displayName,
  navigateToChannelTab,
  navigateToChannel,
  isLoading,
}: ContextMenuTabListProps) => {
  const allTabs = buildTabList({
    channelUuid,
    displayName,
    isLoading,
    msChannelId,
    navigateToChannel,
    navigateToChannelTab,
    tabs,
  });

  return (
    <>
      {allTabs.map((tab) => (
        <ContextMenuItem
          key={tab.id}
          label={tab.displayName}
          loading={tab.loading}
          onClick={tab.onClick}
          type={tab.type}
        />
      ))}
    </>
  );
};
